<template>

  <div class="form-check">
    <label class="checkbox blue" :for="id">
      <input 
        type="checkbox"
        :value="value"
        class="form-check_input"
        :class="{ 'error': !!errorMessage}"
        :name="name"
        :id="id"
        :required="required"
        :aria-label="arialabel"
        :aria-describedby="'texterror' + id"
        :aria-invalid="!!errorMessage"
        @input="handleChange"
      /><span class="indicator"></span>
      <span class="label-text">{{ label }}</span>
    </label>
    
    <span :id="'texterror' + id" class="error-txt">
      <span v-if="errorMessage">{{ errorMessage }}</span>
    </span>
  </div>

</template>

<script lang="ts" setup>
import { toRef } from 'vue';
import { useField } from 'vee-validate';

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  arialabel: {
    type: String,
    required: false,
  },
  required: { 
    type: Boolean,
    default: false
  },
});

// we are using toRefs to create reactive references to `name` and `value` props
// this is important because vee-validte needs to know if any of these props change
// https://vee-validate.logaretm.com/v4/guide/composition-api/caveats

const name = toRef(props, "name");

const { checked, handleChange, errorMessage } = useField(name, undefined, {
  type: 'checkbox',
  checkedValue: props.value,
});

</script>

<style lang="scss" scoped>
.form-check {
  display: block;
}

.error-txt {
	display: block;
	margin:0.5rem 0 0 0;
	color: $color-bv-logo-eld;
}


/* Checkbox styles */

$black: #2D3137;
$blue: #3785BC;
$green: #329E78;
$grey: #D6D6D6;
$red: #DD3C3A;
$white: #FFFFFF;
$yellow: #f1e530;

$border-radius: 3px;

label.checkbox span.indicator {
    border-radius: 3px;
    position: absolute;
    left: 0;
    top: -1px;
    width: 1.5rem;
    height: 1.5rem;
    background-color: #ffffff;
    border: 2px solid #000000;
    pointer-events: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}

label.checkbox.blue {
    margin-right: 1rem;
    padding-left: 1.75rem;
    position: relative;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    display: flex;
}
label.checkbox.blue input[type=checkbox] {
    position: absolute;
    opacity: 0;
}
label.checkbox.blue input[type=checkbox]:checked ~ span.indicator {
    color: #FFFFFF;
    background: #3785BC url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+DQo8c3ZnIHdpZHRoPSIxMiIgaGVpZ2h0PSI5IiB2aWV3Qm94PSIwIDAgMTIgOSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCiAgPHBhdGggZD0iTTQuNTc1IDguOTc3cy0uNDA0LS4wMDctLjUzNi0uMTY1TC4wNTcgNS42NGwuODI5LTEuMjI3TDQuNDcgNy4yNjggMTAuOTIxLjA4NmwuOTIzIDEuMTAzLTYuODYzIDcuNjRjLS4xMzQtLjAwMy0uNDA2LjE0OC0uNDA2LjE0OHoiIGZpbGw9IiNGRkYiIGZpbGwtcnVsZT0iZXZlbm9kZCIvPg0KPC9zdmc+) 50% 50% no-repeat;
    border: 2px solid #2b6e9e;
}
label.checkbox.blue input[type=checkbox].error ~ span.indicator {
    color: #FFFFFF;
    background: tint($red, 70%) none;
    border: 2px solid $red;
}

label.checkbox.blue input[type=checkbox]:focus ~ span.indicator {
  outline: max(2px, 0.15em) solid $color-bv-logo-blue;
  outline-offset: max(2px, 0.15em);
}
.label-text {
  display: inline-block;
  margin-left: 0.3rem;
  font-weight: bold;;
}

</style>
